import React from "react";
import "./ReconResponse.css";

const parseReconResponse = (reconResponse) => {
    if (!reconResponse) {
        return null;
    }

    if (typeof reconResponse === "object") {
        return reconResponse;
    }

    try {
        return JSON.parse(reconResponse);
    } catch (e) {
        return null;
    }
};

const getStatusModifier = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    if (normalized === "success") {
        return "recon-response__status--success";
    }
    if (normalized === "failed") {
        return "recon-response__status--failed";
    }
    return "recon-response__status--pending";
};

const MappingNode = ({ label, value }) => (
    <div className="recon-node">
        <span className="recon-node__label">{label}</span>
        <span className="recon-node__value">{value || "-"}</span>
    </div>
);

const MappingFlow = ({ title, source, target, rows }) => (
    <div className="recon-response__mapping">
        <div className="recon-response__mapping-title">{title}</div>
        <div className="recon-response__flows">
            {rows.map((row, index) => (
                <div className="recon-flow" key={index}>
                    <MappingNode label={source.label} value={row?.[source.key]} />
                    <span className="recon-flow__connector" />
                    <MappingNode label={target.label} value={row?.[target.key]} />
                </div>
            ))}
        </div>
    </div>
);

/**
 * Renders the recon outcome for a request.
 * Status falls back to "Pending" when recon_status is null but the request
 * has reached "Finance Approved". The body shows request_id, the
 * pi_to_ti_mapping and ti_to_credit_note_mapping from recon_response.
 */
const ReconResponse = ({ reconStatus, reconResponse }) => {
    const response = parseReconResponse(reconResponse);
    const displayStatus =
        reconStatus === null || reconStatus === undefined || reconStatus === ""
            ? "Pending"
            : reconStatus;

    const piToTiMapping = Array.isArray(response?.pi_to_ti_mapping)
        ? response.pi_to_ti_mapping
        : [];
    const tiToCreditNoteMapping = Array.isArray(response?.ti_to_credit_note_mapping)
        ? response.ti_to_credit_note_mapping
        : [];

    return (
        <div className="recon-response">
            <div className="recon-response__summary">
                <div className="recon-response__item">
                    <span className="recon-response__label">Status</span>
                    <span
                        className={`recon-response__status ${getStatusModifier(displayStatus)}`}
                    >
                        {displayStatus}
                    </span>
                </div>

                {response?.request_id && (
                    <div className="recon-response__item">
                        <span className="recon-response__label">Request Id</span>
                        <span className="recon-response__text">{response.request_id}</span>
                    </div>
                )}
            </div>

            {response?.message && (
                <div
                    className={`recon-response__message ${
                        getStatusModifier(displayStatus) === "recon-response__status--failed"
                            ? "recon-response__message--failed"
                            : ""
                    }`}
                >
                    {response.message}
                </div>
            )}

            <div className="recon-response__tables">
                {piToTiMapping.length > 0 && (
                    <MappingFlow
                        title="PI to TI Mapping"
                        source={{ key: "pi_number", label: "PI Number" }}
                        target={{ key: "new_ti_number", label: "New TI Number" }}
                        rows={piToTiMapping}
                    />
                )}

                {tiToCreditNoteMapping.length > 0 && (
                    <MappingFlow
                        title="TI to Credit Note Mapping"
                        source={{ key: "ti_number", label: "TI Number" }}
                        target={{ key: "credit_note_number", label: "Credit Note Number" }}
                        rows={tiToCreditNoteMapping}
                    />
                )}
            </div>
        </div>
    );
};

export default ReconResponse;
